/**
 * Exams Phase B — Intelligent Examination panels.
 * Extracted from SchoolExams.jsx.
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import {
  Loader2, Sparkles, Trophy, GraduationCap, AlertTriangle, ShieldCheck
} from 'lucide-react';
import { schoolApi as api } from '@/components/school/schoolApi';

const AutoQuizTab = ({ tenantId, subjects, classes }) => {
  const qc = useQueryClient();
  const [form, setForm] = useState({ source: 'mock', title: '', subject_id: '', lesson_id: '', topic: '', bloom_level: '', question_count: 10, time_limit_minutes: 20 });
  const { data: stats } = useQuery({ queryKey: ['taxonomy-stats', tenantId], queryFn: () => api(`/tenants/${tenantId}/question-bank/taxonomy-stats`) });
  const { data: lessons = [] } = useQuery({ queryKey: ['aq-lessons', tenantId], queryFn: () => api(`/tenants/${tenantId}/lessons`) });
  const mut = useMutation({
    mutationFn: () => api(`/tenants/${tenantId}/auto-quiz/generate`, { method: 'POST', body: JSON.stringify(form) }),
    onSuccess: (r) => { toast.success(`Generated quiz · ${r.question_count} questions (easy ${r.by_difficulty.easy || 0} / med ${r.by_difficulty.medium || 0} / hard ${r.by_difficulty.hard || 0})`); qc.invalidateQueries({ queryKey: ['taxonomy-stats'] }); },
    onError: e => toast.error(e.message),
  });
  const BLOOMS = ['remember', 'understand', 'apply', 'analyze', 'evaluate', 'create'];
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="auto-quiz-tab">
      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center gap-2"><Sparkles className="h-5 w-5 text-amber-500" /><h3 className="font-semibold">Auto Quiz Generator</h3></div>
          <p className="text-xs text-slate-500">Build an intelligent quiz by sampling the question bank.</p>
          <div><Label className="text-xs">Source</Label>
            <Select value={form.source} onValueChange={v => setForm({ ...form, source: v })}>
              <SelectTrigger data-testid="auto-quiz-source"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="lesson">Lesson</SelectItem>
                <SelectItem value="week">Last week's lessons</SelectItem>
                <SelectItem value="mock">Mock exam (40/40/20 difficulty mix)</SelectItem>
                <SelectItem value="topic">By topic</SelectItem>
                <SelectItem value="bloom">By Bloom level</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">Title</Label><Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="e.g. Unit 4 recap" data-testid="auto-quiz-title" /></div>
          <div><Label className="text-xs">Subject (optional)</Label>
            <Select value={form.subject_id || 'all'} onValueChange={v => setForm({ ...form, subject_id: v === 'all' ? '' : v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">All subjects</SelectItem>{subjects.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {form.source === 'lesson' && <div><Label className="text-xs">Lesson</Label>
            <Select value={form.lesson_id} onValueChange={v => setForm({ ...form, lesson_id: v })}>
              <SelectTrigger><SelectValue placeholder="Choose lesson…" /></SelectTrigger>
              <SelectContent>{lessons.slice(0, 100).map(l => <SelectItem key={l.id} value={l.id}>{l.title}</SelectItem>)}</SelectContent>
            </Select>
          </div>}
          {form.source === 'topic' && <div><Label className="text-xs">Topic</Label><Input value={form.topic} onChange={e => setForm({ ...form, topic: e.target.value })} placeholder="e.g. Photosynthesis" /></div>}
          {form.source === 'bloom' && <div><Label className="text-xs">Bloom Level</Label>
            <Select value={form.bloom_level} onValueChange={v => setForm({ ...form, bloom_level: v })}>
              <SelectTrigger><SelectValue placeholder="Pick level…" /></SelectTrigger>
              <SelectContent>{BLOOMS.map(b => <SelectItem key={b} value={b} className="capitalize">{b}</SelectItem>)}</SelectContent>
            </Select>
          </div>}
          <div><Label className="text-xs">Question count</Label><Input type="number" value={form.question_count} onChange={e => setForm({ ...form, question_count: Number(e.target.value) })} /></div>
          <Button onClick={() => mut.mutate()} disabled={!form.title || mut.isPending} data-testid="generate-auto-quiz">{mut.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-2" /> : <Sparkles className="h-3.5 w-3.5 mr-1" />}Generate Quiz</Button>
        </CardContent>
      </Card>
      <Card><CardContent className="p-4 space-y-2">
        <h3 className="font-semibold">Question Bank Taxonomy</h3>
        <p className="text-xs text-slate-500">Total questions: <strong>{stats?.total || 0}</strong></p>
        <div className="space-y-3 text-xs">
          <div>
            <p className="font-medium mb-1">By Bloom level</p>
            <div className="flex flex-wrap gap-1.5">{(stats?.by_bloom || []).map(b => <Badge key={b.level} variant="outline" className="capitalize">{b.level} · {b.count}</Badge>)}</div>
          </div>
          <div>
            <p className="font-medium mb-1">By difficulty</p>
            <div className="flex flex-wrap gap-1.5">{(stats?.by_difficulty || []).map(b => <Badge key={b.level} variant="outline" className="capitalize">{b.level} · {b.count}</Badge>)}</div>
          </div>
          <div>
            <p className="font-medium mb-1">Top topics</p>
            <div className="flex flex-wrap gap-1.5">{(stats?.by_topic || []).slice(0, 10).map(t => <Badge key={t.topic} variant="outline">{t.topic} · {t.count}</Badge>)}</div>
          </div>
        </div>
      </CardContent></Card>
    </div>
  );
};

// ==================== PHASE B — PROCTORING TAB ====================
const ProctoringTab = ({ tenantId, exams }) => {
  const [examId, setExamId] = useState('');
  const { data: events = [] } = useQuery({ queryKey: ['proctor-events', tenantId, examId], queryFn: () => api(`/tenants/${tenantId}/exams/${examId}/proctoring`), enabled: !!examId });
  const { data: summary } = useQuery({ queryKey: ['proctor-summary', tenantId, examId], queryFn: () => api(`/tenants/${tenantId}/exams/${examId}/proctoring-summary`), enabled: !!examId });
  const sevColor = { info: 'bg-slate-100 text-slate-700', warning: 'bg-amber-100 text-amber-800', violation: 'bg-red-100 text-red-700' };
  return (
    <div data-testid="proctoring-tab">
      <Card className="mb-3"><CardContent className="p-4 flex items-center gap-3">
        <Label className="text-xs">Select Exam</Label>
        <Select value={examId} onValueChange={setExamId}>
          <SelectTrigger className="w-full max-w-[320px]" data-testid="proctor-exam-select"><SelectValue placeholder="Pick exam…" /></SelectTrigger>
          <SelectContent>{(exams || []).map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
        </Select>
      </CardContent></Card>
      {!examId && <p className="text-center text-slate-400 py-10 text-sm">Pick an exam to view proctoring report.</p>}
      {examId && summary && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card><CardContent className="p-4">
            <h3 className="font-semibold text-sm mb-2">Risk leaderboard</h3>
            <div className="space-y-1 max-h-[320px] overflow-y-auto">
              {summary.flagged_students.length === 0 && <p className="text-xs text-slate-400 text-center py-4">No flagged students.</p>}
              {summary.flagged_students.map(s => (
                <div key={s.student_id} className="flex justify-between items-center text-xs border rounded p-2" data-testid={`flagged-${s.student_id}`}>
                  <div>
                    <p className="font-medium">{s.student_name}</p>
                    <p className="text-[10px] text-slate-500">V: {s.violation} · W: {s.warning} · I: {s.info}</p>
                  </div>
                  <Badge className="bg-red-100 text-red-700">Risk {s.risk_score}</Badge>
                </div>
              ))}
            </div>
            <p className="text-[11px] text-slate-400 mt-2">Events: {summary.total_events} · Violations: <strong className="text-red-600">{summary.total_violations}</strong></p>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <h3 className="font-semibold text-sm mb-2">Recent events</h3>
            <div className="space-y-1 max-h-[360px] overflow-y-auto">
              {events.slice(0, 100).map(e => (
                <div key={e.id} className="text-xs border rounded p-2 flex justify-between" data-testid={`proctor-event-${e.id}`}>
                  <div>
                    <p className="font-medium">{e.student_name || e.student_id} · <span className="text-slate-500">{e.event_type.replace(/_/g, ' ')}</span></p>
                    <p className="text-[10px] text-slate-400">{e.details || ''} · {e.created_at ? new Date(e.created_at).toLocaleString() : ''}</p>
                  </div>
                  <Badge className={`${sevColor[e.severity]} text-[10px]`}>{e.severity}</Badge>
                </div>
              ))}
              {events.length === 0 && <p className="text-xs text-slate-400 text-center py-4">No events yet.</p>}
            </div>
          </CardContent></Card>
        </div>
      )}
    </div>
  );
};

// ==================== PHASE B — EXAM DASHBOARD TAB ====================
const ExamDashboardTab = ({ tenantId }) => {
  const { data } = useQuery({ queryKey: ['admin-exam-dash', tenantId], queryFn: () => api(`/tenants/${tenantId}/admin-exam-dashboard`) });
  if (!data) return <div className="text-center py-10"><Loader2 className="h-5 w-5 animate-spin inline" /></div>;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="exam-dashboard-tab">
      <Card><CardContent className="p-4">
        <h3 className="font-semibold text-sm mb-2">Upcoming exams ({data.upcoming.length})</h3>
        <div className="space-y-1 max-h-[260px] overflow-y-auto">
          {data.upcoming.length === 0 && <p className="text-xs text-slate-400 text-center py-4">None scheduled.</p>}
          {data.upcoming.map(e => (
            <div key={e.id} className="text-xs border rounded p-2" data-testid={`upcoming-exam-${e.id}`}>
              <p className="font-medium">{e.title}</p>
              <p className="text-[10px] text-slate-400">{e.start_date} · <span className="capitalize">{e.status}</span></p>
            </div>
          ))}
        </div>
      </CardContent></Card>
      <Card><CardContent className="p-4">
        <h3 className="font-semibold text-sm mb-2 flex items-center gap-1"><AlertTriangle className="h-3.5 w-3.5 text-amber-500" />Missing marks</h3>
        <div className="space-y-1 max-h-[260px] overflow-y-auto">
          {data.missing_marks.length === 0 && <p className="text-xs text-slate-400 text-center py-4">No missing marks 🎉</p>}
          {data.missing_marks.map(m => (
            <div key={m.exam_id} className="text-xs border rounded p-2" data-testid={`missing-${m.exam_id}`}>
              <p className="font-medium">{m.title}</p>
              <p className="text-[10px] text-slate-500">{m.subjects_graded}/{m.subjects_total} subjects graded · <span className="text-red-600">{m.subjects_missing} missing</span></p>
            </div>
          ))}
        </div>
      </CardContent></Card>
      <Card><CardContent className="p-4">
        <h3 className="font-semibold text-sm mb-2 flex items-center gap-1"><ShieldCheck className="h-3.5 w-3.5 text-red-500" />Proctoring violations</h3>
        <div className="space-y-1 max-h-[260px] overflow-y-auto">
          {data.violations_leaderboard.length === 0 && <p className="text-xs text-slate-400 text-center py-4">No violations logged.</p>}
          {data.violations_leaderboard.map(v => (
            <div key={v.student_id} className="flex justify-between text-xs border rounded p-2" data-testid={`violation-${v.student_id}`}>
              <span>{v.student_name}</span>
              <Badge className="bg-red-100 text-red-700">{v.event_count}</Badge>
            </div>
          ))}
        </div>
      </CardContent></Card>
      <Card className="md:col-span-2 lg:col-span-3"><CardContent className="p-4">
        <h3 className="font-semibold text-sm mb-2 flex items-center gap-1"><Trophy className="h-3.5 w-3.5 text-amber-500" />Top performers (recent exams)</h3>
        {data.top_performers.length === 0 && <p className="text-xs text-slate-400 text-center py-4">No graded marks yet.</p>}
        {data.top_performers.length > 0 && (
          <Table>
            <TableHeader><TableRow><TableHead>#</TableHead><TableHead>Student</TableHead><TableHead>Exams</TableHead><TableHead>Average</TableHead></TableRow></TableHeader>
            <TableBody>
              {data.top_performers.map((p, i) => (
                <TableRow key={p.student_id} data-testid={`top-performer-${p.student_id}`}>
                  <TableCell>{i + 1}</TableCell>
                  <TableCell className="text-sm">{p.student_name}</TableCell>
                  <TableCell className="text-xs">{p.exams}</TableCell>
                  <TableCell><Badge className={p.avg_percent >= 80 ? 'bg-emerald-100 text-emerald-700' : p.avg_percent >= 60 ? 'bg-indigo-100 text-indigo-700' : 'bg-amber-100 text-amber-700'}>{p.avg_percent}%</Badge></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent></Card>
    </div>
  );
};

// ==================== PHASE B — PARENT WEEKLY DIGEST TAB ====================
const ParentDigestTab = ({ tenantId, classes }) => {
  const [classId, setClassId] = useState('');
  const [days, setDays] = useState(7);
  const [dryRun, setDryRun] = useState(true);
  const [previewStudent, setPreviewStudent] = useState('');
  const { data: preview } = useQuery({ queryKey: ['digest-preview', tenantId, previewStudent, days], queryFn: () => api(`/tenants/${tenantId}/parents/weekly-digest/preview?student_id=${previewStudent}&days=${days}`), enabled: !!previewStudent });
  const { data: students = [] } = useQuery({ queryKey: ['digest-students', tenantId, classId], queryFn: async () => {
    const base = classId ? `/tenants/${tenantId}/students?class_id=${classId}&limit=100` : `/tenants/${tenantId}/students?limit=100`;
    const r = await api(base);
    return r.students || [];
  } });
  const sendMut = useMutation({
    mutationFn: () => api(`/tenants/${tenantId}/parents/weekly-digest/send`, { method: 'POST', body: JSON.stringify({ class_id: classId || null, days, dry_run: dryRun }) }),
    onSuccess: (r) => { toast.success(`${r.digests_prepared} digests prepared · ${r.emails_sent} emails sent${r.dry_run ? ' (dry run)' : ''}${r.email_configured ? '' : ' · SMTP NOT configured'}`); },
    onError: e => toast.error(e.message),
  });
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="parent-digest-tab">
      <Card><CardContent className="p-4 space-y-3">
        <div className="flex items-center gap-2"><GraduationCap className="h-5 w-5 text-indigo-500" /><h3 className="font-semibold">Weekly Engagement Digest</h3></div>
        <p className="text-xs text-slate-500">Auto-compile each child's attendance, engagement, pending quizzes and recent exam results — then email every guardian.</p>
        <div><Label className="text-xs">Class (optional)</Label>
          <Select value={classId || 'all'} onValueChange={v => setClassId(v === 'all' ? '' : v)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">All classes</SelectItem>{classes.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label className="text-xs">Window (days)</Label><Input type="number" value={days} onChange={e => setDays(Number(e.target.value) || 7)} data-testid="digest-days" /></div>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={dryRun} onChange={e => setDryRun(e.target.checked)} data-testid="digest-dry-run" />Dry run (compile only; don't email)</label>
        <Button onClick={() => sendMut.mutate()} disabled={sendMut.isPending} data-testid="send-digest-btn">{sendMut.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-2" /> : <Sparkles className="h-3.5 w-3.5 mr-1" />}{dryRun ? 'Dry-run digest' : 'Send digests to all guardians'}</Button>
      </CardContent></Card>
      <Card><CardContent className="p-4 space-y-3">
        <h3 className="font-semibold text-sm">Preview for one student</h3>
        <Select value={previewStudent} onValueChange={setPreviewStudent}>
          <SelectTrigger data-testid="digest-preview-student"><SelectValue placeholder="Pick student…" /></SelectTrigger>
          <SelectContent>{students.slice(0, 100).map(s => <SelectItem key={s.id} value={s.id}>{s.first_name} {s.last_name}</SelectItem>)}</SelectContent>
        </Select>
        {preview && (
          <div className="space-y-1 text-xs bg-slate-50 p-3 rounded" data-testid="digest-preview">
            <p><strong>{preview.student_name}</strong></p>
            <p>📘 Live classes: {preview.live_classes_total} · Attendance: {preview.attendance_percent ?? 'N/A'}%</p>
            <p>🎯 Engagement: {preview.avg_engagement ?? 'N/A'}/100 · Missed: {preview.missed_sessions}</p>
            <p>📝 Exam avg: {preview.recent_exam_avg ?? 'N/A'}%</p>
            <p>📋 Pending quizzes: {preview.pending_pop_quizzes.length}</p>
            <div className="mt-2 p-2 bg-indigo-50 text-indigo-900 rounded">💡 {preview.recommended_action}</div>
          </div>
        )}
      </CardContent></Card>
    </div>
  );
};



export { AutoQuizTab, ProctoringTab, ExamDashboardTab, ParentDigestTab };
